import React from 'react';
import { DocumentCarouselItem } from './DocumentCarouselItem';
import { AcknowledgementDocumentCarouselCard } from '../../views/document/AcknowledgementDocumentCarouselCard';
import { Carousel, CarouselSlider } from '@fluentui/react-carousel-preview';
import { ReportType } from '../../graphql/schema/p4types';
import { IDocumentPrint, SpecialReportType } from './DocumentProvider';
import { PickingInstructionDocumentCarouselCard } from '../../views/document/PickingInstructionDocumentCarouselCard';
import { QuotationDocumentCarouselCard } from '../../views/document/QuotationDocumentCarouselCard';
import { PaymentReceiptDocumentCarouselCard } from '../../views/document/PaymentReceiptDocumentCarouselCard';
import { ProformaInvoiceDocumentCarouselCard } from '../../views/document/ProformaInvoiceDocumentCarouselCard';
import { SalesOrderLabelDocumentCarouselCard } from '../../views/document/SalesOrderLabelDocumentCarouselCard';
import { AssemblyPickingInstructionDocumentCarouselCard } from '../../views/document/AssemblyPickingInstructionDocumentCarouselCard';
import { StockTakeDocumentCarouselCard } from '../../views/document/StockTakeDocumentCarouselCard';
import { DispatchInstructionDocumentCarouselCard } from '../../views/document/DispatchInstructionDocumentCarouselCard';
import { ConformityCertificateDocumentCarouselCard } from '../../views/document/ConformityCertificateDocumentCarouselCard';
import { DispatchLabelDocumentCarouselCard } from '../../views/document/DispatchLabelDocumentCarouselCard';

const CarouselItem = ({ documentPrint }: { documentPrint: IDocumentPrint }) => {
  switch (documentPrint.reportType) {
    case ReportType.SalesAcknowledgement:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-acknowledgement'}
          key={'document-carousel-acknowledgement'}
        >
          <AcknowledgementDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.SalesQuote:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-quotation-document'}
          key={'document-carousel-quotation-document'}
        >
          <QuotationDocumentCarouselCard actionCriteria={documentPrint.actionCriteria} documentPrint={documentPrint} />
        </DocumentCarouselItem>
      );
    case ReportType.ProformaInvoice:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-proforma-invoice'}
          key={'document-carousel-proforma-invoice'}
        >
          <ProformaInvoiceDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.PaymentReceipt:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-payment-receipt'}
          key={'document-carousel-payment-receipt'}
        >
          <PaymentReceiptDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.SalesPickingList:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-sales-picking-list'}
          key={'document-carousel-sales-picking-list'}
        >
          <PickingInstructionDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.SalesOrderLabel:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-sales-order-label'}
          key={'document-carousel-sales-order-label'}
        >
          <SalesOrderLabelDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.AssemblyPickingList:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-sales-assembly-picking'}
          key={'document-carousel-sales-assembly-picking'}
        >
          <AssemblyPickingInstructionDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.StockTakeReport:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-stock-take-report'}
          key={'document-carousel-stock-take-report'}
        >
          <StockTakeDocumentCarouselCard actionCriteria={documentPrint.actionCriteria} documentPrint={documentPrint} />
        </DocumentCarouselItem>
      );
    case ReportType.SalesDeliveryNote:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-dispatch-instruction'}
          key={'document-carousel-dispatch-instruction'}
        >
          <DispatchInstructionDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case ReportType.ConformityCertificate:
      return (
        <DocumentCarouselItem
          data-testid={'document-carousel-conformity-certificate'}
          key={'document-carousel-conformity-certificate'}
        >
          <ConformityCertificateDocumentCarouselCard
            actionCriteria={documentPrint.actionCriteria}
            documentPrint={documentPrint}
          />
        </DocumentCarouselItem>
      );
    case SpecialReportType.DispatchLabel:
      return (
        <DocumentCarouselItem data-testid={'document-carousel-dispatch-label'} key={'document-carousel-dispatch-label'}>
          <DispatchLabelDocumentCarouselCard actionCriteria={documentPrint.actionCriteria} />
        </DocumentCarouselItem>
      );
  }
  return null;
};

/**
 * Document Carousel Implementation Guide:
 * - Add a 'DocumentTypeDocumentCarouselCard' component based on AcknowledgementDocumentCarouselCard (Or if it's a no-customer/no-supplier document, template from PickingInstructionDocumentCarouselCard)
 *   -- Update references in the file accordingly. This step includes creating a 'DocumentTypeDocumentCarouselQuery.graphql' in graphql/queries/document.
 *   -- Take care to consider any additional options specific to the Document that need adding to the Modal content for functional parity.
 * - Add an entry to the switch in the DocumentCarousel element below to point at the new type.
 * - Update anywhere that renders the old DocumentModal to call 'printDocumentSingle'/'printDocumentActionEntities' instead via the 'useP4DocumentController' hook.
 *   -- (Note - For the moment, ensure that we check the 'enableDocumentCarousel' feature flag and don't remove the old methodology - That'll be for a future version).
 */

/**
 * Render a Carousel Component containing the provided list of Documents.
 * @param activeIndex - The currently active index (0 on initial mount - 'onSkip/onBack' will update this via context).
 * @param documents - The full list of documents currently being printed (Usually just 1, but Sales Order save may generate multiple documents at once).
 */
export const DocumentCarousel = ({
  activeIndex,
  documents,
}: {
  activeIndex: number;
  documents: Array<IDocumentPrint>;
}) => {
  return (
    <Carousel activeIndex={activeIndex}>
      <CarouselSlider>
        {documents.map((doc) => (
          <CarouselItem key={doc.reportType} documentPrint={doc} />
        ))}
      </CarouselSlider>
    </Carousel>
  );
};
